import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Send } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CONTACT_EMAIL, CONTACT_PHONE, CONTACT_PHONE_TEL } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", phone: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || form.message.trim().length < 5) {
      toast.error("Please fill in your name, a valid email, and a message.");
      return;
    }
    try {
      setSubmitting(true);
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! Our team will reply within 1 business day.");
      setForm(initial);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        eyebrow="Contact"
        title="We're here. Reach out anytime."
        subtitle="Questions about a transfer, KYC, or compliance? We typically respond within one business day."
      />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-6">
          <a href={`tel:${CONTACT_PHONE_TEL}`} className="flex items-start gap-4 rounded-2xl border border-slate-200 p-5 hover:border-[#FF6B00] transition-colors" data-testid="contact-phone-card">
            <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FF6B00] flex items-center justify-center"><Phone size={20} /></div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Phone</div>
              <div className="font-display text-lg font-semibold text-[#0B192C]">{CONTACT_PHONE}</div>
              <div className="text-xs text-slate-500 mt-1">Mon–Fri · 9am–6pm MT</div>
            </div>
          </a>

          <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-start gap-4 rounded-2xl border border-slate-200 p-5 hover:border-[#FF6B00] transition-colors" data-testid="contact-email-card">
            <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FF6B00] flex items-center justify-center"><Mail size={20} /></div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Email</div>
              <div className="font-display text-lg font-semibold text-[#0B192C] break-all">{CONTACT_EMAIL}</div>
              <div className="text-xs text-slate-500 mt-1">We reply within 24 hours</div>
            </div>
          </a>

          <div className="flex items-start gap-4 rounded-2xl border border-slate-200 p-5" data-testid="contact-location-card">
            <div className="w-10 h-10 rounded-xl bg-orange-50 text-[#FF6B00] flex items-center justify-center"><MapPin size={20} /></div>
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Office</div>
              <div className="font-display text-lg font-semibold text-[#0B192C]">Calgary, Alberta</div>
              <div className="text-xs text-slate-500 mt-1">Canada</div>
            </div>
          </div>
        </div>

        <form onSubmit={onSubmit} className="lg:col-span-3 rounded-3xl border border-slate-200 p-6 sm:p-8 bg-white" data-testid="contact-form">
          <h2 className="font-display text-2xl font-bold text-[#0B192C]">Send us a message</h2>
          <p className="mt-1 text-sm text-slate-600">We'll get back to you at the email you provide.</p>

          <div className="grid sm:grid-cols-2 gap-4 mt-6">
            <div>
              <Label htmlFor="name">Name *</Label>
              <Input id="name" name="name" value={form.name} onChange={onChange} placeholder="Your full name" data-testid="contact-name-input" className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="email">Email *</Label>
              <Input id="email" type="email" name="email" value={form.email} onChange={onChange} placeholder="you@example.com" data-testid="contact-email-input" className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" name="phone" value={form.phone} onChange={onChange} placeholder="(optional)" data-testid="contact-phone-input" className="mt-1.5 h-11 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="subject">Subject</Label>
              <Input id="subject" name="subject" value={form.subject} onChange={onChange} placeholder="How can we help?" data-testid="contact-subject-input" className="mt-1.5 h-11 rounded-xl" />
            </div>
          </div>

          <div className="mt-4">
            <Label htmlFor="message">Message *</Label>
            <Textarea id="message" name="message" value={form.message} onChange={onChange} placeholder="Share the details…" rows={6} data-testid="contact-message-input" className="mt-1.5 rounded-xl" />
          </div>

          <Button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit-button"
            className="mt-6 rounded-full bg-[#FF6B00] hover:bg-[#E65C00] text-white h-12 px-7 font-semibold shadow-brand disabled:opacity-60"
          >
            {submitting ? "Sending…" : <>Send message <Send size={16} className="ml-2" /></>}
          </Button>
        </form>
      </section>
    </div>
  );
}
